import type { MouseEvent, FunctionComponent } from 'react';
import { Component, useCallback } from 'react';
import { Alert } from 'react-bootstrap';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import * as _ from 'lodash';
import qs from 'qs';

import { PREMIUM_COMP_SEC_PLAN_NAME } from '../../../shared/subscriptions';
import { window } from '../../globals';
import { useAsyncValue } from '../../hooks';
import { get } from '../../redux/api';
import OrganizationsModule from '../../redux/organizations';
import TeamsModule from '../../redux/teams';
import VulnsModule from '../../redux/vulns';
import type { IAsyncStatus, TeamSelection } from '../../types';
import utils, { hasError, showDelightedSurveyImmediately } from '../../utils';
import { ErrorAlert } from '../generic';
import Button from '../generic/Button';
import DropdownButton from '../generic/DropdownButton';
import { IconText, icons } from '../generic/Icon';
import Loader from '../generic/Loader';
import Text from '../generic/Text';
import ModalBulkReport from '../modals/ModalBulkReport';
import type { MessageOptions } from '../modals/ModalPlanSelector';
import DashboardReportCard from './DashboardReportCard';
import { useCanDisplaySecurityOptions } from '../projects/generic/hooks';
import {
  userHasPermissionToCreateOrgReports,
  userHasPermissionToCreateTeamReports,
} from '../../helpers/permissions';
import { auditDueDiligenceReportFeature, issueDashboardFeature, licensePackageReportFeature } from '../settings/billing/features/consts';

type CheckAccessParams = {
  event?: MouseEvent<HTMLElement>;
  rejectMessageOptions?: MessageOptions;
};

type DashboardActionCardsProps = {
  createAuditReport(teamIds?: number[]): Promise<void>;
  createSecurityExport(): Promise<void>;
  onCreateIssueCSVExportClick: (teamIds?: TeamSelection) => Promise<void>;
  selectedTeams: TeamSelection;
  organizationsStatus: IAsyncStatus;
  creatingAuditReportLoading: boolean;
  creatingSecurityReportLoading: boolean;
  isIssueCSVExportLoading: boolean;
  isSecurityAndPDFReportsEnabled: boolean;
};

type DashboardActionCardsState = {
  showingModalBulkReport: boolean;
  successAlert: string | null;
};

type RenderBaseExportOptions = {
  title: string;
  description: string;
  onClick: (...arg: any[]) => Promise<void> | void;
  propsLoadingField: boolean;
  emailDeliveryMessage?: string;
};

const SUCCESS_MESSAGE_TIMEOUT = 5000;

const getGlobalIssuesCSVMessage = (method?: 'email' | 'download') => {
  if (method === 'email') {
    return 'Your report is being generated and will be emailed to you';
  }

  return 'Your report will begin downloading shortly.';
};

const GenerateReportIconText = (
  <IconText icon={icons.addToProject} text="Generate report" />
);

class DashboardActionCards extends Component<DashboardActionCardsProps, DashboardActionCardsState> {
  constructor(props) {
    super(props);
    this.state = {
      showingModalBulkReport: false,
      successAlert: null,
    };
  }

  componentDidMount() {
    showDelightedSurveyImmediately();
  }

  hideMessage = () => this.setState({ successAlert: null });

  checkAccess = ({ event, rejectMessageOptions }: CheckAccessParams): boolean => {
    // TODO: utils.verify_subscription should be able to differentiate between security or no security premium plans.
    // It's fine right now because we hide the security reports unless the org has security.
    if (!utils.verify_subscription('premium')) {
      window.showPlanSelectorModal(rejectMessageOptions);

      if (event) {
        event.preventDefault();
      }
      return false;
    }

    return true;
  };

  generateReport = async (params: {
    ev?: MouseEvent<HTMLElement>;
    submitFn: (...args: any[]) => Promise<void>;
    successAlert?: string | null;
    rejectMessageOptions?: MessageOptions;
    args?: unknown[];
  }) => {
    if (!this.checkAccess({ event: params?.ev, rejectMessageOptions: params.rejectMessageOptions })) {
      return;
    }

    try {
      this.setState({ successAlert: null });
      await params.submitFn(...(params.args || []));
      this.setState({
        successAlert: params.successAlert ?? null,
      });
      setTimeout(() => this.hideMessage, SUCCESS_MESSAGE_TIMEOUT);
    } catch (e) {
      // Error is handled by Redux.
    }
  };

  generateAudit = async (ev: MouseEvent<HTMLElement>) => {
    const args = [_.isEmpty(this.props.selectedTeams) ? undefined : this.props.selectedTeams];

    await this.generateReport({
      ev,
      submitFn: this.props.createAuditReport,
      successAlert: 'Your report is being generated and will be emailed to you.',
      rejectMessageOptions: {
        focusFeature: auditDueDiligenceReportFeature,
      },
      args,
    });
  };

  generateCSVExport = async (method?: 'email' | 'download') => {
    await this.generateReport({
      submitFn: async () => {
        const teamIds = this.props.selectedTeams;
        if (method === 'email') {
          this.props.onCreateIssueCSVExportClick(teamIds);
        } else {
          const params = _.isEmpty(this.props.selectedTeams)
            ? ''
            : `?${qs.stringify({ teamIds })}`;
          utils.openInNewTab(`/api/v2/issues/csv/global${params}`);
        }
      },
      rejectMessageOptions: {
        focusFeature: issueDashboardFeature,
      },
      successAlert: getGlobalIssuesCSVMessage(method),
    });
  };

  generateSecurityExport = async () => {
    await this.generateReport({
      submitFn: this.props.createSecurityExport,
      rejectMessageOptions: {
        tier: PREMIUM_COMP_SEC_PLAN_NAME,
      },
      successAlert: 'Your report is being generated and will be emailed to you.',
    });
  };

  generateReportBundle = () => {
    const hasAccess = this.checkAccess({
      rejectMessageOptions: {
        focusFeature: licensePackageReportFeature,
      },
    });

    if (!hasAccess) {
      return;
    }
    this.setState({ showingModalBulkReport: true });
  };

  renderAuditReportGlobalTeamFilter = () => (
    <div>
      <DashboardReportCard
        title="Audit/Due Diligence Report"
        description={(
          <p>
            An organization-wide report on Issues and project changes.
            <em>The report will be emailed to you.</em>
          </p>
        )}
      >
        <div>
          {this.props.creatingAuditReportLoading ? (
            <div style={{ width: '100px', marginLeft: '60px' }}>
              <Loader size={26} />
            </div>
          ) : (
            <Button type="secondary" size="large" onClick={this.generateAudit}>
              {GenerateReportIconText}
            </Button>
          )}
        </div>
      </DashboardReportCard>
    </div>
  );

  renderBaseExport = (options: RenderBaseExportOptions) => {
    return (
      <DashboardReportCard
        title={options.title}
        description={options.description}
        emailDeliveryMessage={options.emailDeliveryMessage}
      >
        <div>
          {options.propsLoadingField ? (
            <div style={{ width: '100px', marginLeft: '60px' }}>
              <Loader size={26} />
            </div>
          ) : (
            <Button type="secondary" size="large" onClick={options.onClick}>
              {GenerateReportIconText}
            </Button>
          )}
        </div>
      </DashboardReportCard>
    );
  };

  render() {
    // Report creation is limited to Org level users or Team level users with edit permissions.
    const canCreateReport = userHasPermissionToCreateOrgReports() || userHasPermissionToCreateTeamReports();
    return (
      <div className="column dashboard-actions">
        {hasError(this.props.organizationsStatus) && <ErrorAlert status={this.props.organizationsStatus} />}
        {this.state.successAlert && <Alert bsStyle="success">{this.state.successAlert}</Alert>}
        {canCreateReport ? (
          <div className="reportPanel">
            {this.renderBaseExport({
              title: 'Global Package Report Bundle',
              description: 'An organization-wide report on licenses and packages.',
              onClick: this.generateReportBundle,
              propsLoadingField: false,
              emailDeliveryMessage: 'The report will be emailed to you.',
            })}

            {this.renderAuditReportGlobalTeamFilter()}

            <div>
              <DashboardReportCard
                title="Global Issue CSV Export"
                description="Organization-wide issue reports in CSV format. Contains separate reports for license, security, and quality issues."
              >
                <DropdownButton
                  id="GlobalCSVExportV2Options"
                  title={GenerateReportIconText}
                  size="large"
                  onSelect={this.generateCSVExport}
                  disabled={this.props.isIssueCSVExportLoading}
                >
                  <DropdownButton.Item eventKey="download">Download</DropdownButton.Item>
                  <DropdownButton.Item eventKey="email">Email</DropdownButton.Item>
                </DropdownButton>
              </DashboardReportCard>
            </div>

            {/* this deprecated report (available via FF) is not available for Team Report users,
            only OrgReport users as it does not respect the Team filters required for TeamReport usage */}
            {this.props.isSecurityAndPDFReportsEnabled && userHasPermissionToCreateOrgReports() &&
              this.renderBaseExport({
                title: 'Global Security Issue Export',
                description:
                  'An organization-wide issue export in PDF format. Contains a detailed breakdown of security issues.',
                onClick: this.generateSecurityExport,
                propsLoadingField: this.props.creatingSecurityReportLoading,
              })}
          </div>
        ) : (
          <div className="reportPanel">
            <div className="panel animated fadeInDown delay-animation-long reportInfo">
              <div>
                <Text.H1>Generating Reports</Text.H1>
                <hr />
                <p>You are not permitted to create organization-wide reports.</p>
              </div>
            </div>
          </div>
        )}

        <ModalBulkReport
          show={this.state.showingModalBulkReport}
          onHide={() => this.setState({ showingModalBulkReport: false })}
        />
      </div>
    );
  }
}

/**
 * A sort-of higher-order component that wraps the DashboardActionCards component and
 * provides the issue CSV export AsyncValue as a prop. This used to be in the IssuesV1
 * Redux store, but was moved into a simple hook as part of deleting the IssuesV1 store.
 * Also provides the result of the useCanDisplaySecurityOptions hook as a prop.
 */
const DashboardActionCardsWithIssueCSVExport: FunctionComponent<
  Omit<
    DashboardActionCardsProps,
    | 'onCreateIssueCSVExportClick'
    | 'isIssueCSVExportLoading'
    | 'isSecurityAndPDFReportsEnabled'
  >
> = (props) => {
  const isSecurityAndPDFReportsEnabled = useCanDisplaySecurityOptions();

  const asyncValue = useAsyncValue(null);
  const emailGlobalIssueCSVExport = useCallback(async (teamIds?: TeamSelection) => {
    //  Note that we are intentionally not catching errors here. This is because the component does that already in the submitFn.
    asyncValue.setPending();
    const url = '/api/v2/issues/csv/global';
    const params = {
      email: true,
    };
    // if no teamIds are selected in the global filter we don't want to send the param
    if (!_.isEmpty(teamIds)) {
      Object.assign(params, { teamIds });
    }
    await get(url, false, params);
    asyncValue.setResolve(null);
  }, [asyncValue]);

  return (
    <DashboardActionCards
      isIssueCSVExportLoading={asyncValue.isLoading}
      onCreateIssueCSVExportClick={emailGlobalIssueCSVExport}
      isSecurityAndPDFReportsEnabled={isSecurityAndPDFReportsEnabled}
      // eslint-disable-next-line react/jsx-props-no-spreading -- It's fine
      {...props}
    />
  );
};

const mapStateToProps = createStructuredSelector({
  selectedTeams: TeamsModule.selectors.getTeamSelection,
  organizationsStatus: OrganizationsModule.selectors.getStatus,
  creatingAuditReportLoading: OrganizationsModule.selectors.getCreatingAuditReportLoading,
  creatingSecurityReportLoading: VulnsModule.selectors.getCreatingSecurityExportLoading,
});

const mapDispatchToProps = {
  createAuditReport: OrganizationsModule.actions.createAuditReport,
  createSecurityExport: VulnsModule.actions.createOrgSecurityReport,
};

export default connect(mapStateToProps, mapDispatchToProps)(DashboardActionCardsWithIssueCSVExport);
